// Wheel of Fate (docs/04 §17.3): a step-up wheel with three rings. The player stops it; a stop inside
// the timing band (reported by the view, or rolled by the auto-resolver) spins once more and keeps
// the better segment. STEP IN moves to the next ring. Night Shift prizes grant free spins.
import { B } from '../../content/balance';
import { D, type Decimal } from '../decimal';
import type { Modifiers } from '../modifiers';
import type { Rng } from '../rng';
import type { WheelBonus } from '../types';

/** Coin segments are `c<par>` (e.g. `c20`); `charm` is not one of them. */
export function coinPar(seg: string): number | null {
  const m = /^c(\d+)$/.exec(seg);
  return m ? Number(m[1]) : null;
}

/** Night Shift segments are `n<spins>`. */
export function nightSpins(seg: string): number | null {
  const m = /^n(\d+)$/.exec(seg);
  return m ? Number(m[1]) : null;
}

export function wheelSegments(ring: number): readonly string[] {
  return B.WHEEL_RINGS[Math.min(ring, B.WHEEL_RINGS.length - 1)]!;
}

export function createWheel(par: Decimal): WheelBonus {
  return { mode: 'wheel', par, ring: 0, stops: [], prizes: [], done: false, payout: D(0), voided: false, night: 0 };
}

/** The prize a segment really gives (the Plug segment is plain coins off the good path). */
export function segmentPrize(seg: string, plugLive: boolean): string {
  return seg === 'plug' && !plugLive ? 'c150' : seg;
}

export function prizeRank(seg: string, ring: number): number {
  if (seg === 'step') return B.WHEEL_PRIZE_RANK[`step${ring}`] ?? 0;
  const coins = coinPar(seg);
  if (coins !== null) return coins;
  const night = nightSpins(seg);
  if (night !== null) return night * (B.WHEEL_PRIZE_RANK.night ?? 3);
  return B.WHEEL_PRIZE_RANK[seg] ?? 0;
}

function rollSegment(ring: number, mods: Modifiers, rng: Rng): number {
  const segs = wheelSegments(ring);
  return rng.weighted(segs.map((s) => (s === 'step' ? mods.wheelStepX : 1)));
}

/** Stop the wheel. Returns the segment landed on (the caller applies non-coin prizes). */
export function wheelStop(b: WheelBonus, hit: boolean, mods: Modifiers, rng: Rng, plugLive: boolean): string {
  const segs = wheelSegments(b.ring);
  let idx = rollSegment(b.ring, mods, rng);
  if (hit) {
    const again = rollSegment(b.ring, mods, rng);
    const rank = (i: number) => prizeRank(segmentPrize(segs[i]!, plugLive), b.ring);
    if (rank(again) > rank(idx)) idx = again;
  }
  const seg = segmentPrize(segs[idx]!, plugLive);
  b.stops.push(idx);
  b.prizes.push(seg);
  if (seg === 'step' && b.ring < B.WHEEL_RINGS.length - 1) {
    b.ring++;
    return seg;
  }
  const coins = coinPar(seg);
  const night = nightSpins(seg);
  if (coins !== null) b.payout = b.par.mul(coins).floor();
  else if (night !== null) b.night = night;
  b.done = true;
  return seg;
}
